/**
 * The category picker on the category reports (owner, 2026-09-23: "in the
 * detailed category sales i should be able to select the categories i want").
 *
 * Pure, so the rules that decide what the server is asked for are pinned
 * without a browser.
 *
 * THREE STATES, NOT TWO. `null` is every category — no filter at all, which is
 * also what the report shows before anyone touches the picker. A list is those
 * categories. An EMPTY list is nothing ticked: the server reads an empty filter
 * as "all" (`reportCategoryIdsParam`), so that state is never sent — the page
 * asks for a tick instead. Without it, "Clear" would land on exactly the same
 * report as "Select all", and the usual way to pick two categories — clear,
 * then tick two — would flash the whole menu in between.
 *
 * THE SELECTION LIVES IN THE URL (`?categories=a,b`) so a refresh or a shared
 * link opens on the same categories.
 */
import { isCategoryFilterableReport, type ReportId } from '@gc/shared';

/**
 * Reports that take the filter beyond the contract's list — every other report
 * that prints a product Category column (owner: "any other report that lists
 * categories, if it fits naturally"): the Product Sales Report and the three
 * stock reports. Their builders honour `categoryIds` the same way
 * (apps/api/src/reports/category-filter.test.ts, which mirrors this list), so
 * they get the picker as well until `CATEGORY_FILTERABLE_REPORT_IDS` names them.
 */
const ALSO_CATEGORY_FILTERABLE: readonly ReportId[] = [
  'cafe.products',
  'stores.lowStock',
  'stores.margin',
  'stores.valuation',
];

/** Whether a report's page shows the category picker. */
export function showsCategoryFilter(id: ReportId): boolean {
  return isCategoryFilterableReport(id) || ALSO_CATEGORY_FILTERABLE.includes(id);
}

/**
 * Lines with no category — none ever, or one that has since been deleted (the
 * picker is built from today's menu, so a deleted category has no box of its
 * own; its rows answer to this one, labelled "Uncategorised or deleted").
 * Mirrors `UNCATEGORISED_REPORT_CATEGORY_ID` in
 * apps/api/src/reports/category-filter.ts — the key the report already groups
 * category-less lines under — so the two must change together.
 */
export const REPORT_UNCATEGORISED_ID = '__none__';

/** The page's search param. */
export const CATEGORY_PARAM = 'categories';

/** `null` = every category; `[]` = nothing ticked; otherwise the ticked ids. */
export type CategorySelection = readonly string[] | null;

export interface CategoryOption {
  id: string;
  name: string;
}

/** Read the param: absent is every category, present-but-empty is none. */
export function parseCategoryParam(value: string | null): CategorySelection {
  if (value === null) return null;
  return [
    ...new Set(
      value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== ''),
    ),
  ];
}

/** Write the param back; `null` removes it. */
export function serialiseCategoryParam(selection: CategorySelection): string | null {
  return selection === null ? null : selection.join(',');
}

/** Which ids are ticked, with `null` expanded to every option. */
export function tickedIds(
  selection: CategorySelection,
  options: readonly CategoryOption[],
): Set<string> {
  return new Set(selection ?? options.map((option) => option.id));
}

/**
 * The canonical form of a selection against the options on offer.
 *
 * - ids not on the menu are dropped (a link can outlive its category), and if
 *   that leaves a list that HAD ids but now has none, it is every category —
 *   the server makes the same call, so the chip and the figures agree;
 * - every option ticked collapses to `null`, so "Select all" is the plain
 *   report and not a long list in the URL;
 * - the order follows the menu, so the same selection is always the same URL.
 *
 * With no options known yet the selection is left alone: nothing can be
 * judged unknown against a list that has not loaded.
 */
export function normaliseCategorySelection(
  selection: CategorySelection,
  options: readonly CategoryOption[],
): CategorySelection {
  if (selection === null || options.length === 0) return selection;
  const wanted = new Set(selection);
  const kept = options.filter((option) => wanted.has(option.id)).map((option) => option.id);
  if (kept.length === 0) return selection.length === 0 ? [] : null;
  if (kept.length === options.length) return null;
  return kept;
}

/** Tick or untick one category. */
export function toggleCategory(
  selection: CategorySelection,
  id: string,
  options: readonly CategoryOption[],
): CategorySelection {
  const ticked = tickedIds(selection, options);
  if (!ticked.delete(id)) ticked.add(id);
  return normaliseCategorySelection([...ticked], options);
}

/**
 * "Select all" over the options currently listed — every option with no
 * search, or just the matches while one is typed ("drinks", then Select all).
 */
export function selectCategories(
  selection: CategorySelection,
  ids: readonly string[],
  options: readonly CategoryOption[],
): CategorySelection {
  const ticked = tickedIds(selection, options);
  for (const id of ids) ticked.add(id);
  return normaliseCategorySelection([...ticked], options);
}

/** "Clear" over the options currently listed; clearing all of them is `[]`. */
export function clearCategories(
  selection: CategorySelection,
  ids: readonly string[],
  options: readonly CategoryOption[],
): CategorySelection {
  const ticked = tickedIds(selection, options);
  for (const id of ids) ticked.delete(id);
  return normaliseCategorySelection([...ticked], options);
}

/** The options whose name contains the search, case-insensitively. */
export function matchCategoryOptions(
  options: readonly CategoryOption[],
  search: string,
): CategoryOption[] {
  const needle = search.trim().toLocaleLowerCase();
  if (needle === '') return [...options];
  return options.filter((option) => option.name.toLocaleLowerCase().includes(needle));
}

/** What the folded chip says: every category, none, or how many of how many. */
export type CategorySummary =
  { kind: 'all' } | { kind: 'none' } | { kind: 'some'; count: number; total: number | null };

export function summariseCategorySelection(
  selection: CategorySelection,
  options: readonly CategoryOption[],
): CategorySummary {
  if (selection === null) return { kind: 'all' };
  if (selection.length === 0) return { kind: 'none' };
  // Before the menu loads there is nothing to count against.
  return {
    kind: 'some',
    count: selection.length,
    total: options.length > 0 ? options.length : null,
  };
}

/**
 * The ids to send with the report request: `undefined` for every category,
 * `[]` for none (the page does not fetch), otherwise the list.
 */
export function categoryIdsForRequest(selection: CategorySelection): string[] | undefined {
  return selection === null ? undefined : [...selection];
}
